import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { I18n } from "react-redux-i18n";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useUnmount } from "react-use";
import { Box, Button, Grid, TextField, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import FormButton from "./FormButton";
import { Auth } from "aws-amplify";
import { AUTHENTICATOR_RESET, AUTHENTICATOR_SIGNUP } from "./constants";

const ForgotPassword = ({ initialValue, setInitialValue, setAuthScreen }) => {
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(null);
  const theme = useTheme();

  const schema = useMemo(() => {
    return yup.object({
      email: yup
        .string()
        .required(I18n.t("authenticator.required"))
        .email(I18n.t("authenticator.emailRequired")),
    });
  }, []);

  const {
    reset,
    handleSubmit,
    control,
    getValues,
    formState: { errors },
    formState: { isSubmitted },
  } = useForm({
    defaultValues: {
      email: initialValue.email,
    },
    resolver: yupResolver(schema),
  });

  const emailErrorMsg = useMemo(() => {
    if (error) return I18n.t(`authenticator.${error}`);

    return errors?.email?.message || "";
  }, [errors?.email?.message, error]);

  useEffect(() => {
    reset({
      email: initialValue.email,
    });
  }, [initialValue, reset]);

  useUnmount(() => {
    setInitialValue({ ...initialValue, ...getValues() });
  });

  const onSubmit = async (data) => {
    setError(null);
    setLoader(true);

    try {
      await Auth.forgotPassword(data.email);

      return setAuthScreen(AUTHENTICATOR_RESET);
    } catch (e) {
      console.log(e);

      if (e.code === "UserNotFoundException") setError("UserNotFoundException");

      if (e.code === "NotAuthorizedException") setError("NotAuthorizedException2");
    }

    setLoader(false);
  };

  return (
    <Grid
      container
      spacing={2}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Typography
          variant="h4"
          color="primary"
          style={{
            fontFamily: "'Paytone One', sans- serif",
            marginBottom: "4px",
          }}
        >
          {I18n.t("authenticator.forgotPasswordTitle")}
        </Typography>

        <Typography style={{ color: theme.palette.auth.readonly }}>
          {I18n.t("authenticator.forgotPasswordMessage")}
        </Typography>
      </Grid>

      <Grid item xs={12} style={{ display: "flex", flexDirection: "column" }}>
        <Controller
          name="email"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextField
              label={I18n.t("authenticator.email")}
              variant="outlined"
              {...field}
              error={(isSubmitted && Boolean(errors.email)) || Boolean(error)}
              helperText={emailErrorMsg}
            />
          )}
        />
      </Grid>

      <Box style={{ alignSelf: "center", margin: "20px auto" }}>
        <FormButton {...{ label: I18n.t("authenticator.send"), loader }} />
      </Box>

      <Grid
        item
        xs={12}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography style={{ marginRight: "4px" }} color="textSecondary">
          {I18n.t("authenticator.notMember")}
        </Typography>

        <Button
          type="submit"
          color="primary"
          onClick={() => setAuthScreen(AUTHENTICATOR_SIGNUP)}
        >
          {I18n.t("authenticator.signUp")}
        </Button>
      </Grid>
    </Grid>
  );
};

export default ForgotPassword;
